import React, { useState } from 'react';
import { Link } from 'react-router-dom';
import api from '../api/axios';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '../components/ui/card';
import { toast } from 'sonner';
import { ArrowLeft, Mail, Lock, ShieldCheck, Loader2 } from 'lucide-react';

export default function ForgotPasswordPage() {
  const [step, setStep] = useState(1);
  const [email, setEmail] = useState('');
  const [loading, setLoading] = useState(false);

  // Emergencia
  const [showEmergency, setShowEmergency] = useState(false);
  const [secretKey, setSecretKey] = useState('');
  const [newPassword, setNewPassword] = useState('');
  const [confirmPassword, setConfirmPassword] = useState('');

  const handleRequestEmail = async (e) => {
    e.preventDefault();
    setLoading(true);
    try {
      await api.post('/auth/forgot-password', { email });
      toast.success('Email enviado com sucesso!');
      setStep(2);
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Erro ao solicitar recuperacao');
    } finally {
      setLoading(false);
    }
  };

  const handleEmergencyReset = async (e) => {
    e.preventDefault();
    if (newPassword !== confirmPassword) {
      toast.error('As senhas nao coincidem!');
      return;
    }
    setLoading(true);
    try {
      await api.post('/auth/emergency-reset', { email, secret_key: secretKey, new_password: newPassword });
      toast.success('Senha redefinida com sucesso!');
      setStep(3);
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Erro no reset de emergencia');
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="min-h-screen flex items-center justify-center bg-gradient-to-br from-[#003366] to-[#001a33] p-4">
      <Card className="w-full max-w-md" data-testid="forgot-password-card">
        <CardHeader className="text-center">
          <div className="mx-auto mb-4 w-16 h-16 bg-[#003366] rounded-full flex items-center justify-center">
            <Lock className="text-white" size={32} />
          </div>
          <CardTitle className="text-2xl text-[#003366]">Recuperar Senha</CardTitle>
          <CardDescription>
            {step === 1 && !showEmergency && 'Informe seu email para receber o link de recuperacao'}
            {step === 1 && showEmergency && 'Reset de emergencia para administradores'}
            {step === 2 && 'Verifique sua caixa de entrada'}
            {step === 3 && 'Senha alterada com sucesso!'}
          </CardDescription>
        </CardHeader>

        <CardContent>
          {/* Step 1: Solicitar email */}
          {step === 1 && !showEmergency && (
            <form onSubmit={handleRequestEmail} className="space-y-4">
              <div className="space-y-2">
                <Label htmlFor="email">Email cadastrado</Label>
                <div className="relative">
                  <Mail className="absolute left-3 top-3 text-gray-400" size={18} />
                  <Input
                    id="email"
                    type="email"
                    value={email}
                    onChange={(e) => setEmail(e.target.value)}
                    placeholder="seu@email.com"
                    className="pl-10"
                    required
                    data-testid="forgot-email-input"
                  />
                </div>
              </div>
              
              <Button type="submit" className="w-full bg-[#003366] hover:bg-[#002244]" disabled={loading} data-testid="forgot-submit-btn">
                {loading ? <><Loader2 className="animate-spin mr-2" size={16} /> Enviando...</> : 'Enviar Link de Recuperacao'}
              </Button>

              <div className="text-center space-y-2">
                <Link to="/login" className="text-sm text-[#0066CC] hover:underline flex items-center justify-center gap-1">
                  <ArrowLeft size={16} /> Voltar ao Login
                </Link>
                <button type="button" onClick={() => setShowEmergency(true)} className="text-xs text-gray-500 hover:text-gray-700">
                  Acesso de emergencia (Admin)
                </button>
              </div>
            </form>
          )}

          {/* Emergencia */}
          {step === 1 && showEmergency && (
            <form onSubmit={handleEmergencyReset} className="space-y-4">
              <div className="bg-amber-50 border border-amber-200 rounded-lg p-3">
                <p className="text-sm text-amber-800"><ShieldCheck className="inline mr-1" size={16} />Reset de emergencia com chave secreta</p>
              </div>
              <div className="space-y-2">
                <Label>Email</Label>
                <Input type="email" value={email} onChange={(e) => setEmail(e.target.value)} placeholder="admin@email.com" required />
              </div>
              <div className="space-y-2">
                <Label>Chave Secreta</Label>
                <Input type="password" value={secretKey} onChange={(e) => setSecretKey(e.target.value)} placeholder="Chave de emergencia" required />
              </div>
              <div className="space-y-2">
                <Label>Nova Senha</Label>
                <Input type="password" value={newPassword} onChange={(e) => setNewPassword(e.target.value)} placeholder="Minimo 6 caracteres" required />
              </div>
              <div className="space-y-2">
                <Label>Confirmar Senha</Label>
                <Input type="password" value={confirmPassword} onChange={(e) => setConfirmPassword(e.target.value)} placeholder="Repita a senha" required />
              </div>
              <Button type="submit" className="w-full bg-[#CC0000] hover:bg-[#990000]" disabled={loading}>
                {loading ? 'Processando...' : 'Redefinir Senha'}
              </Button>
              <button type="button" onClick={() => setShowEmergency(false)} className="w-full text-sm text-gray-500 hover:text-gray-700">Voltar ao modo normal</button>
            </form>
          )}

          {/* Step 2: Email enviado */}
          {step === 2 && (
            <div className="text-center space-y-4">
              <div className="mx-auto w-16 h-16 bg-blue-100 rounded-full flex items-center justify-center">
                <Mail className="text-blue-600" size={32} />
              </div>
              <p className="text-gray-600 leading-relaxed">
                Enviamos um link de recuperacao para<br />
                <strong className="text-[#003366]">{email}</strong>
              </p>
              <div className="bg-blue-50 border border-blue-200 rounded-lg p-4 text-left">
                <p className="text-sm text-blue-800 font-medium mb-2">Verifique:</p>
                <ul className="text-sm text-blue-700 space-y-1">
                  <li>- Sua caixa de entrada</li>
                  <li>- A pasta de spam/lixo eletronico</li>
                </ul>
                <p className="text-xs text-blue-600 mt-3">O link expira em 30 minutos.</p>
              </div>
              <div className="space-y-2 pt-2">
                <Button variant="outline" className="w-full" onClick={() => setStep(1)}>
                  Enviar novamente
                </Button>
                <Link to="/login">
                  <Button variant="ghost" className="w-full text-[#0066CC]">
                    <ArrowLeft size={16} className="mr-1" /> Voltar ao Login
                  </Button>
                </Link>
              </div>
            </div>
          )}

          {/* Step 3: Sucesso */}
          {step === 3 && (
            <div className="text-center space-y-4">
              <div className="mx-auto w-16 h-16 bg-green-100 rounded-full flex items-center justify-center">
                <ShieldCheck className="text-green-600" size={32} />
              </div>
              <p className="text-gray-600">Sua senha foi alterada com sucesso!<br />Voce ja pode fazer login.</p>
              <Link to="/login">
                <Button className="w-full bg-[#003366] hover:bg-[#002244]">Ir para o Login</Button>
              </Link>
            </div>
          )}
        </CardContent>
      </Card>
    </div>
  );
}
